'use client'

import { trackPricingModelPricesExpanded } from '@/lib/analytics'
import { useState } from 'react'
import { ChevronDown, Search } from '@/components/ui/icons'
import { usdToOlives } from '@/lib/marketing-config'

export interface PriceRow {
  key: string
  name: string
  /** Tier label ("1080p with audio"); only on expanded rows. */
  variant?: string
  usd: number
  /** Collapsed rows quote the cheapest tier of a multi-tier model. */
  from?: boolean
  /** Per-row unit when it differs from the group's ("/min", "/1k chars"). */
  unit?: string
}

export interface PriceGroup {
  id: string
  title: string
  /** Default unit suffix for the group: "/s", "/image", "/clip", "/set". */
  unit: string
  /** The handful shown before expanding. */
  featured: PriceRow[]
  /** Every tier of every model, shown on expand. Empty = nothing to expand. */
  all: PriceRow[]
  /** Model count for the expand button ("Show all 27 video models"). */
  modelCount: number
  noun: string
}

// Every per-unit price to three decimals, so the column lines up and cheap models never round to $0.
const fmtUsd = (n: number) => `$${n.toFixed(3)}`
const fmtOlives = (n: number) => usdToOlives(n).toFixed(2)
// Per-second models also show what a 10-second clip costs: the number people actually budget with.
const CLIP_SECONDS = 10
const fmtClip = (n: number) => `$${(n * CLIP_SECONDS).toFixed(2)}`

const TEASE_ROWS = 4

// The teaser is the popular models, whole (every tier of each), until it reaches TEASE_ROWS.
function teaser(group: PriceGroup, all: PriceRow[]) {
  const rows: PriceRow[] = []
  for (const { key } of group.featured) {
    if (rows.length >= TEASE_ROWS) break
    rows.push(...all.filter(r => r.key === key))
  }
  return rows.length ? rows : all.slice(0, TEASE_ROWS)
}

const cols = (perSecond: boolean) =>
  `grid items-baseline gap-4 ${perSecond ? 'grid-cols-[minmax(0,1fr)_88px_72px_88px]' : 'grid-cols-[minmax(0,1fr)_120px_72px]'}`

// A search shows every matching row of every group; without one, each group teases and unfolds by itself.
function Group({ group, query }: { group: PriceGroup; query: string }) {
  const [expanded, setExpanded] = useState(false)
  const all = group.all.length ? group.all : group.featured
  const rows = query
    ? all.filter(r => `${r.name} ${r.variant ?? ''}`.toLowerCase().includes(query))
    : expanded
      ? all
      : teaser(group, all)
  const hidden = all.length - rows.length
  if (query && !rows.length) return null
  const perSecond = group.unit === '/s'
  return (
    <section aria-labelledby={`prices-${group.id}`}>
      <div className={`${cols(perSecond)} border-b border-white/10 pb-3`}>
        <h3 id={`prices-${group.id}`} className="text-[20px] font-semibold tracking-[-0.01em] text-white">
          {group.title}
        </h3>
        <span className="text-end text-xs text-white/40">USD{perSecond ? ' / s' : ''}</span>
        <span className="text-end text-xs text-white/40">Olives</span>
        {perSecond && <span className="text-end text-xs text-white/40">{CLIP_SECONDS}s clip</span>}
      </div>
      <ul>
        {rows.map((row, i) => {
          // A model's later tiers show only the tier label: "Seedance 2.5 / 720p / 1080p".
          const named = i === 0 || rows[i - 1].key !== row.key
          return (
            <li
              key={`${row.key}-${row.variant ?? ''}`}
              className={`${cols(perSecond)} border-b border-white/[0.06] py-3 text-[15px] tabular-nums`}
            >
              <span className="min-w-0 truncate text-white/85">
                {named ? row.name : null}
                {row.variant && (
                  <span className={`text-[13px] text-white/40 ${named ? 'ms-2' : 'ps-4'}`}>{row.variant}</span>
                )}
              </span>
              <span className="text-end text-white">
                {row.usd === 0 ? 'Free' : fmtUsd(row.usd)}
                {!perSecond && row.usd > 0 && <span className="text-white/40">{row.unit ?? group.unit}</span>}
              </span>
              <span className="text-end text-white/45">{row.usd === 0 ? '—' : fmtOlives(row.usd)}</span>
              {perSecond && <span className="text-end text-white/70">{fmtClip(row.usd)}</span>}
            </li>
          )
        })}
      </ul>
      {!query && (hidden > 0 || expanded) && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => {
            setExpanded(!expanded)
            if (!expanded) trackPricingModelPricesExpanded({ group: group.id })
          }}
          className="mt-3 inline-flex items-center gap-1.5 text-sm text-white/50 transition-colors hover:text-white"
        >
          {expanded ? 'Show fewer' : `+ ${hidden} more`}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      )}
    </section>
  )
}

// One quiet rate card, one column, each group unfolding on its own.
export function ModelPrices({ groups }: { groups: PriceGroup[] }) {
  const [search, setSearch] = useState('')
  const query = search.trim().toLowerCase()
  return (
    <div className="max-w-[880px]">
      <div className="relative">
        <Search
          className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40"
          aria-hidden="true"
        />
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search models"
          aria-label="Search models"
          className="h-12 w-full rounded-xl border border-white/10 bg-white/[0.03] pe-4 ps-11 text-[15px] text-white placeholder:text-white/40 focus:border-white/30 focus:outline-none"
        />
      </div>
      <div className="mt-10 space-y-14">
        {groups.map(group => (
          <Group key={group.id} group={group} query={query} />
        ))}
        {/* Each group hides itself when nothing in it matches; this is the all-hidden case. */}
        <p className="hidden text-[15px] text-white/50 first:block">No models match “{search.trim()}”.</p>
      </div>
    </div>
  )
}
